import type { KeyboardEvent } from "react";

/**
 * Keys mapped to what they activate on the element that owns the handler. Values are optional so a
 * caller can build one map and leave a shortcut out (e.g. while its action is unavailable) without
 * branching on the shape.
 */
export type KeyActivationMap = Record<string, (() => void) | undefined>;

/**
 * Gives ONE element several keyboard activations — the answer for a composite control that must stay a
 * single tab stop (a grid cell holding a row of icon actions) while still being fully operable from the
 * keyboard. Pair it with `aria-keyshortcuts` so the shortcuts are announced rather than guessed at.
 *
 * Two guards keep it from firing when it shouldn't:
 *  - only the element that OWNS the map may trigger it (`target === currentTarget`), so a key pressed in
 *    a descendant control — or in a dialog these actions open, since a React portal still bubbles
 *    through the React tree — never doubles as the container's shortcut;
 *  - a held Alt/Ctrl/Meta means the user is aiming at a browser or OS shortcut, not this one.
 */
export const activateOnKeyMap =
	(actions: KeyActivationMap) =>
	(event: KeyboardEvent<HTMLElement>): void => {
		if (event.target !== event.currentTarget) return;
		if (event.altKey || event.ctrlKey || event.metaKey) return;
		const action = actions[event.key] ?? actions[event.key.toLowerCase()];
		if (!action) return;
		event.preventDefault();
		action();
	};

/**
 * Makes a non-semantic clickable (a styled `Box`) keyboard-operable: activate on Enter or Space, matching
 * native button behavior. Pair it with `role="button"`, `tabIndex={0}`, and an `aria-label` so screen-reader
 * and keyboard users can reach the monthly grid/sidebar controls the same way pointer users can.
 */
export const activateOnKey = (onActivate: () => void) => activateOnKeyMap({ Enter: onActivate, " ": onActivate });
